import { hubViewForContentEntityType } from "@/components/hub/hub-view-for-entity";
import { useHubStore } from "@/components/hub/use-hub-store";
import { getActivityConfig } from "@/lib/activities/registry";
import { useShareableEntity } from "@/lib/api/shareable-entities";
import { parseMessageData } from "@/lib/utils/parse-message-data";
import { useCallback, useMemo } from "react";

export interface EntityInteractionData {
  interaction_type: "comment" | "reaction";
  entity_type: "note" | "moment" | "swap" | "activity";
  entity_id: string;
  activity_type?: string;
  preview_url?: string | null;
  second_preview_url?: string | null;
  note_preview?: string | null;
  metadata?: {
    index?: number;
  };
}

export function useEntityInteraction(message: { data?: string }) {
  const entityData = useMemo(
    () => parseMessageData<EntityInteractionData>(message.data),
    [message.data],
  );

  const isActivityInteraction = entityData?.entity_type === "activity";
  const shareableEntityId = entityData?.entity_id ?? "";
  const {
    data: entity,
    isError,
    isPending,
  } = useShareableEntity(shareableEntityId, undefined, {
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
  });
  const isUnavailable = isError || !!entity?.metadata?.takedown;

  const openToView = useHubStore((state) => state.openToView);

  const activityConfig =
    entityData?.entity_type === "activity" && entityData.activity_type
      ? getActivityConfig(entityData.activity_type)
      : undefined;
  const index = entityData?.metadata?.index ?? 0;

  // Interaction messages already exist in chat; render thumbnails for any
  // registered activity type regardless of rollout feature flags.
  const isEnabled =
    !isActivityInteraction || Boolean(activityConfig);

  const openInHub = useCallback(() => {
    if (!entityData || !shareableEntityId || isUnavailable) return;
    if (activityConfig) {
      openToView(activityConfig.hubView, shareableEntityId, index);
      return;
    }
    if (
      entityData.entity_type === "note" ||
      entityData.entity_type === "moment" ||
      entityData.entity_type === "swap"
    ) {
      openToView(
        hubViewForContentEntityType(entityData.entity_type),
        shareableEntityId,
      );
    }
  }, [
    entityData,
    shareableEntityId,
    isUnavailable,
    activityConfig,
    index,
    openToView,
  ]);

  return {
    entityData,
    entity,
    isPending,
    isUnavailable,
    activityConfig,
    isEnabled,
    index,
    openInHub,
  };
}
